import apiService from './api';

class BagService {
  // Get all user bags with caching
  async getUserBags(userId) {
    try {
      const data = await apiService.get(`/api/bag/${userId}`, {
        cacheTimeout: 30000 // Cache for 30 seconds
      });
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  // Get bags with their items and pricing info in one call (optimized)
  async getUserBagsWithItems(userId) {
    try {
      // Try to get cached version first
      const cacheKey = `GET:/api/bag/${userId}/with-items`;
      const cached = apiService.getCachedResponse(cacheKey, 45000); // 45 second cache
      if (cached) {
        // Add role to cached data
        const bagsWithRole = Array.isArray(cached) ? cached.map(bag => ({
          ...bag,
          role: 'owner',
          archived: !!bag.archived,
        })) : [];
        return { success: true, data: bagsWithRole };
      }

      // Use optimized endpoint that returns bags with items in single request
      const data = await apiService.get(`/api/bag/${userId}/with-items`, {
        cacheTimeout: 45000
      });
      
      const bags = Array.isArray(data) ? data : [];
      
      // Add role to each bag
      const bagsWithRole = bags.map(bag => ({
        ...bag,
        role: 'owner',
        archived: !!bag.archived,
      }));
      
      return { success: true, data: bagsWithRole };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  // Get collaborator bags (bags the user collaborates on) including items and pricing (optimized)
  async getCollaboratorBagsWithItems(userId) {
    try {
      // Cache first
      const cacheKey = `GET:collab-bags-with-items-${userId}`;
      const cached = apiService.getCachedResponse(cacheKey, 45000);
      if (cached) {
        return { success: true, data: cached };
      }

      // Fetch collaborations for the user
      const collabResponse = await apiService.get(`/api/bag/user/${userId}/collaborations`, {
        cacheTimeout: 30000
      });

      const collaborations = Array.isArray(collabResponse?.collaborations)
        ? collabResponse.collaborations
        : [];

      // Build a bag-like list from collaborations (only accepted collaborations)
      const collabBags = collaborations
        .filter(c => (c.status || '').toLowerCase() === 'accepted' && c.bag)
        .map(c => {
          const b = c.bag || {};
          // Normalize fields to align with owner bag structure used in UI
          return {
            id: b.id,
            name: b.name,
            description: b.description || '',
            // Backend may use is_public; UI expects private boolean
            private: typeof b.is_public === 'boolean' ? !b.is_public : (b.private ?? false),
            created_at: b.created_at,
            updated_at: b.updated_at || b.created_at,
            image_url: b.image_url || '',
            emoji: b.emoji || '🎒',
            tags: Array.isArray(b.tags) ? b.tags : [],
            user_id: b.owner_id || b.user_id,
            owner_name: b.owner_name || b.ownerFullName || null,
            owner_username: b.owner_username || null,
            ownerFullName: b.owner_name || b.ownerFullName || null,
            owner: b.owner_name || b.owner_username || null,
            role: 'collaborator',
            archived: !!b.archived,
          };
        })
        .filter(b => !!b.id);

      if (collabBags.length === 0) {
        return { success: true, data: [] };
      }

      // Use bulk items endpoint to fetch items for all collaborator bags in one request
      const bagIds = collabBags.map(bag => bag.id);
      const itemsByBag = await apiService.post('/api/item/bulk', { bag_ids: bagIds }, {
        cacheTimeout: 30000
      });

      // Combine bags with their items
      const collabBagsWithItems = collabBags.map(bag => {
        const items = Array.isArray(itemsByBag[bag.id]) ? itemsByBag[bag.id] : [];
        const totalPrice = items.reduce((sum, item) => {
          return sum + ((item.price || 0) * (item.quantity || 1));
        }, 0);

        return {
          ...bag,
          totalPrice,
          itemCount: items.length,
          items
        };
      });

      // Cache result
      apiService.setCacheEntry(cacheKey, collabBagsWithItems, 45000);

      return { success: true, data: collabBagsWithItems };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  /** Paginated My Bags (owner + collaborator) with server-side filter, sort, and search */
  async getMyBagsPage(userId, options = {}) {
    const {
      limit = 8,
      offset = 0,
      filter: bagFilter = 'all',
      sort = 'modified',
      order = 'desc',
      q = '',
      /** Avoid stale list: new URL bypasses in-flight dedupe + old cache keys */
      bustCache = false,
    } = options;
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      params.set('filter', bagFilter);
      params.set('sort', sort);
      params.set('order', order);
      const qt = (q || '').trim();
      if (qt) {
        params.set('q', qt);
      }
      if (bustCache) {
        params.set('_cb', String(Date.now()));
      }
      const data = await apiService.get(
        `/api/bag/${userId}/my-bags-page?${params.toString()}`,
        { cacheTimeout: 0, includeAuth: true }
      );
      const bags = Array.isArray(data.bags) ? data.bags : [];
      const normalized = bags.map((b) => ({
        ...b,
        role: b.role || 'owner',
        archived: !!b.archived,
      }));
      return {
        success: data.success !== false,
        data: normalized,
        total_count: data.total_count ?? 0,
        has_more: !!data.has_more,
        tab_counts: data.tab_counts,
        limit: data.limit ?? limit,
        offset: data.offset ?? offset,
      };
    } catch (error) {
      console.error('BagService.getMyBagsPage error:', error);
      return {
        success: false,
        error: error.message,
        data: [],
        total_count: 0,
        has_more: false,
        tab_counts: null,
      };
    }
  }

  // Get single bag by ID (public access allowed)
  async getBagById(bagId, includeAuth = true) {
    try {
      const data = await apiService.get(`/api/bag/single/${bagId}`, {
        cacheTimeout: 60000, // Cache single bag for 1 minute
        allowUnauthorized: true // This is a public endpoint, don't force logout on 401
      });
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        code: error.code,
        status: error.status,
        canUnarchive: error.canUnarchive,
      };
    }
  }

  // Create new bag
  async createBag(bagData) {
    try {
      console.log('Creating bag:', bagData);
      const data = await apiService.post('/api/bag', bagData);
      // Clear related caches
      apiService.clearCache(`GET:/api/bag/${bagData.user_id}`);
      apiService.clearCache(`GET:/api/bag/${bagData.user_id}/with-items`);
      apiService.clearCacheIf((key) => key.includes('/my-bags-page'));
      return { success: true, data };
    } catch (error) {
      console.error('Error creating bag:', error);
      return { success: false, error: error.message };
    }
  }

  // Update bag
  async updateBag(bagId, bagData, options = {}) {
    try {
      const data = await apiService.put(`/api/bag/${bagId}`, bagData);
      // Clear related caches
      apiService.clearCache(`GET:/api/bag/single/${bagId}`);
      apiService.clearCache(`GET:/api/bag/${bagId}`);
      if (bagData.user_id) {
        apiService.clearCache(`GET:/api/bag/${bagData.user_id}`);
        apiService.clearCache(`GET:/api/bag/${bagData.user_id}/with-items`);
      }
      if (options.userId) {
        apiService.clearCache(`GET:/api/bag/${options.userId}`);
        apiService.clearCache(`GET:/api/bag/${options.userId}/with-items`);
      }
      if (typeof bagData.archived === 'boolean') {
        const cacheKeys = Array.from(apiService.cache.keys());
        cacheKeys.forEach((key) => {
          if (
            key.includes('/api/bag/') ||
            key.includes('collab-bags-with-items')
          ) {
            apiService.clearCache(key);
          }
        });
      }
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        code: error.code,
      };
    }
  }

  // Delete bag
  async deleteBag(bagId) {
    try {
      await apiService.delete(`/api/bag/${bagId}`);
      // Clear caches - we don't know user_id here, so clear all bag-related caches
      const cacheKeys = Array.from(apiService.cache.keys());
      cacheKeys.forEach(key => {
        if (key.includes('/api/bag/') || key.includes('bags-with-items')) {
          apiService.clearCache(key);
        }
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Set all items in a bag to To Buy (not_yet_purchased). Owner only; nothing is deleted. */
  async resetBagItemStatuses(bagId) {
    try {
      const data = await apiService.post(`/api/bag/${bagId}/items/reset-status`, {});
      const cacheKeys = Array.from(apiService.cache.keys());
      cacheKeys.forEach(key => {
        if (
          key.includes('/api/bag/') ||
          key.includes('bags-with-items') ||
          key.includes('collab-bags-with-items')
        ) {
          apiService.clearCache(key);
        }
      });
      apiService.clearCache(`GET:/api/item/${bagId}`);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Share bag
  async shareBag(bagId, shareData) {
    try {
      const data = await apiService.post(`/api/bag/${bagId}/share`, shareData);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Get bag sharing info
  async getBagSharing(bagId) {
    try {
      const data = await apiService.get(`/api/bag/${bagId}/sharing`);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Search bags
  async searchBags(query, userId) {
    try {
      const data = await apiService.get(`/api/bag/search?q=${encodeURIComponent(query)}&userId=${userId}`, {
        allowUnauthorized: true // Public endpoint, don't force logout on 401
      });
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, error: error.message, data: [] };
    }
  }

  // Get lightweight template cards for Explore initial load
  async getTemplateBagCards(offset = 0, searchQuery = '', categories = null, sort = 'popular') {
    const limit = 4;
    const q = (searchQuery || '').trim();
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      params.append('sort', ['popular', 'newest', 'price-low', 'price-high'].includes(sort) ? sort : 'popular');
      if (q) {
        params.append('q', q);
      }
      if (categories != null && categories !== '') {
        params.append('categories', String(categories));
      }

      const cacheKey = `GET:/api/bag/template-cards?${params.toString()}`;
      const cached = apiService.getCachedResponse(cacheKey, 300000); // 5 min
      if (cached) {
        return { success: true, ...cached };
      }

      const response = await apiService.get(`/api/bag/template-cards?${params.toString()}`, {
        allowUnauthorized: true,
        cacheTimeout: 300000 // 5 min
      });

      // Handle both old (array) and new ({ cards, total, has_more }) response shapes
      const cards = Array.isArray(response) ? response : (response.cards || []);
      const total = response.total ?? cards.length;
      const hasMore = response.has_more ?? false;

      const processedCards = cards.map(bag => ({
        ...bag,
        tags: Array.isArray(bag.tags) ? bag.tags : [],
        item_thumbnails: Array.isArray(bag.item_thumbnails) ? bag.item_thumbnails : [],
        itemCount: bag.item_count || 0,
        totalPrice: bag.total_price || 0,
        total_likes: bag.total_likes || 0,
        view_count: bag.view_count ?? bag.views ?? 0,
        views: bag.view_count ?? bag.views ?? 0
      }));

      const result = { cards: processedCards, total, hasMore };
      apiService.setCacheEntry(cacheKey, result, 300000); // 5 min

      return { success: true, ...result };
    } catch (error) {
      console.error('Failed to fetch template bag cards:', error);
      return { success: false, error: error.message, cards: [], total: 0, hasMore: false };
    }
  }

  // Get template bags without authentication (for public template access)
  async getTemplateBags() {
    try {
      // Try to get cached version first (4 hours — templates change rarely)
      const cacheKey = 'GET:/api/bag/templates';
      const cached = apiService.getCachedResponse(cacheKey, 14400000);
      if (cached) {
        return { success: true, data: cached };
      }

      // Use the new templates endpoint that doesn't require authentication
      const bagsWithItems = await apiService.get('/api/bag/templates', {
        allowUnauthorized: true, // Public endpoint, don't force logout on 401
        cacheTimeout: 14400000
      });
      
      const bags = Array.isArray(bagsWithItems) ? bagsWithItems : [];
      
      // Calculate total price and item count for each bag
      const processedBags = bags.map(bag => {
        const items = bag.items || [];
        const totalPrice = items.reduce((sum, item) => {
          return sum + ((item.price || 0) * (item.quantity || 1));
        }, 0);
        
        return {
          ...bag,
          totalPrice,
          itemCount: items.length,
          items
        };
      });
      
      // Cache the result
      apiService.setCacheEntry(cacheKey, processedBags, 14400000);
      
      return { success: true, data: processedBags };
    } catch (error) {
      console.error('Failed to fetch template bags:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  // Admin: fetch featured order (pinned + unpinned template bags)
  async getAdminFeaturedOrder() {
    try {
      const data = await apiService.get('/api/bag/admin/featured-order', { useCache: false });
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message, data: null };
    }
  }

  // Admin: save featured order
  async setAdminFeaturedOrder(bagIds) {
    try {
      const data = await apiService.put('/api/bag/admin/featured-order', { bag_ids: bagIds });
      apiService.clearCacheIf((key) => key.startsWith('GET:/api/bag/template-cards'));
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message, data: null };
    }
  }

  // Create bag from template
  async createBagFromTemplate(userId, templateBagId, bagName = null) {
    try {
      console.log('BagService.createBagFromTemplate called with:', {
        userId,
        templateBagId,
        bagName
      });
      
      const data = await apiService.post(`/api/bag/${userId}/from-template`, {
        template_bag_id: templateBagId,
        bag_name: bagName
      });
      
      console.log('BagService.createBagFromTemplate response:', data);
      
      // Clear related caches
      apiService.clearCache(`GET:/api/bag/${userId}`);
      apiService.clearCache(`GET:/api/bag/${userId}/with-items`);
      
      return { success: true, data: data.data };
    } catch (error) {
      console.error('BagService.createBagFromTemplate error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get public bags for discovery feed
  async getPublicBags(options = {}) {
    const {
      limit = 8,
      offset = 0,
      useCache = true,
      excludeUserId = null,
      q = '',
      categories = null,
      sort = 'popular',
      cursor = null,
    } = options;

    try {
      const qt = (q || '').trim();
      const sortParam = ['popular', 'newest', 'price-low', 'price-high'].includes(sort) ? sort : 'popular';
      let categoriesParam = null;
      if (categories != null && categories !== '') {
        if (Array.isArray(categories)) {
          categoriesParam = [...categories].map((c) => String(c).trim()).filter(Boolean).sort().join(',') || null;
        } else {
          categoriesParam =
            String(categories)
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
              .sort()
              .join(',') || null;
        }
      }
      const excludeSeg =
        excludeUserId != null && String(excludeUserId).trim() !== ''
          ? `&exclude_user_id=${encodeURIComponent(String(excludeUserId).trim())}`
          : '';
      const qSeg = qt ? `&q=${encodeURIComponent(qt)}` : '';
      const catSeg =
        categoriesParam != null && categoriesParam !== ''
          ? `&categories=${encodeURIComponent(categoriesParam)}`
          : '';
      const sortSeg = `&sort=${encodeURIComponent(sortParam)}`;
      const cacheKey = `GET:/api/bag/public?limit=${limit}&offset=${offset}${excludeSeg}${qSeg}${catSeg}${sortSeg}`;

      // Try to get cached version first (only for first page)
      if (useCache && offset === 0 && !cursor) {
        const cached = apiService.getCachedResponse(cacheKey, 1800000); // 30 minute cache for first page
        if (cached) {
          return { success: true, ...cached };
        }
      }

      // Build query params
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      params.append('sort', sortParam);
      if (excludeUserId != null && String(excludeUserId).trim() !== '') {
        params.append('exclude_user_id', String(excludeUserId).trim());
      }
      if (qt) {
        params.append('q', qt);
      }
      if (categoriesParam != null && categoriesParam !== '') {
        params.append('categories', categoriesParam);
      }
      if (cursor) {
        params.append('cursor', cursor);
      }

      const response = await apiService.get(`/api/bag/public?${params.toString()}`, {
        allowUnauthorized: true, // Public endpoint, don't force logout on 401
        cacheTimeout: offset === 0 && !cursor ? 1800000 : 0 // Only cache first page (30 min)
      });
      
      const bags = Array.isArray(response.bags) ? response.bags : [];
      
      const transformedBags = bags.map(bag => ({
        id: bag.id,
        name: bag.name,
        title: bag.name,
        owner: bag.username || bag.profiles?.username || 'unknown_user',
        username: bag.username,
        full_name: bag.full_name || bag.profiles?.full_name,
        tags: bag.tags || [],
        slug: bag.slug,
        thumbnail: bag.image_url || bag.emoji,
        imageUrl: bag.image_url,
        item_thumbnails: bag.item_thumbnails || [],
        total_price: bag.total_price || 0,
        avatar_url: bag.avatar_url || null,
        created_at: bag.created_at,
        updated_at: bag.updated_at,
        item_count: bag.item_count || 0,
        total_likes: bag.total_likes || 0,
        likes: bag.total_likes || 0,
        view_count: bag.view_count ?? bag.views ?? 0,
        views: bag.view_count ?? bag.views ?? 0,
        isPublic: bag.status === 'public',
        private: bag.private,
        description: bag.description,
        emoji: bag.emoji,
        user_id: bag.user_id,
        userId: bag.user_id
      }));
      
      // Prepare result with pagination metadata
      const result = {
        data: transformedBags,
        count: response.count || transformedBags.length,
        total_count: response.total_count ?? null,
        has_more: response.has_more || false,
        limit: response.limit || limit,
        offset: response.offset || offset,
        next_cursor: response.next_cursor || null
      };
      
      // Cache the first page result
      if (offset === 0 && useCache && !cursor) {
        apiService.setCacheEntry(cacheKey, result, 300000);
      }
      
      return { success: true, ...result };
    } catch (error) {
      console.error('Failed to fetch public bags:', error);
      return { success: false, error: error.message, data: [] };
    }
  }


  // Like a bag
  async likeBag(bagId) {
    try {
      const data = await apiService.post(`/api/bag/${bagId}/like`, {});
      apiService.clearCache('GET:/api/bag/public');
      apiService.clearCache('GET:/api/bag/templates');
      apiService.clearCache('GET:/api/bag/template-cards');
      return { success: true, data };
    } catch (error) {
      console.error('Error liking bag:', error);
      return { success: false, error: error.message };
    }
  }

  // Unlike a bag
  async unlikeBag(bagId) {
    try {
      const data = await apiService.delete(`/api/bag/${bagId}/like`);
      apiService.clearCache('GET:/api/bag/public');
      apiService.clearCache('GET:/api/bag/templates');
      apiService.clearCache('GET:/api/bag/template-cards');
      return { success: true, data };
    } catch (error) {
      console.error('Error unliking bag:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user's liked bags
  async getUserLikedBags(userId) {
    try {
      const data = await apiService.get(`/api/bag/user/${userId}/liked`, {
        cacheTimeout: 30000 // Cache for 30 seconds
      });
      return { success: true, data: Array.isArray(data.liked_bags) ? data : [] };
    } catch (error) {
      console.error('Error fetching user liked bags:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

export default new BagService();